import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'react-hot-toast';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function SiteVisitRequest() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    phone: '',
    email: '',
    siteAddress: '',
    preferredDate: '',
    aaBoards: {
      left: 0,
      right: 0,
      straightOn: 0
    },
    fingerBoardCoverage: '',
    maintenanceVisit: '',
    specialRequirements: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAABoardChange = (direction, value) => {
    setFormData(prev => ({
      ...prev,
      aaBoards: {
        ...prev.aaBoards,
        [direction]: parseInt(value) || 0
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/api/site-visit-request`, formData);
      setSubmitted(true);
      toast.success('Site visit request submitted successfully! We will contact you soon.');
    } catch (error) {
      console.error('Site visit request error:', error);
      toast.error('Failed to submit request. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full border-2 border-green-500">
          <CardHeader className="bg-green-50">
            <CardTitle className="text-2xl text-green-800 text-center">Request Submitted Successfully!</CardTitle>
          </CardHeader>
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold mb-3 text-gray-900">Thank You!</h3>
            <p className="text-gray-600 mb-6">
              We've received your site visit request and will be in touch shortly to confirm the details.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/')} 
                className="bg-blue-600 hover:bg-blue-700 text-white w-full"
              >
                Return to Home
              </Button>
              <Button 
                onClick={() => {
                  setSubmitted(false);
                  setFormData({
                    companyName: '',
                    contactPerson: '',
                    phone: '',
                    email: '',
                    siteAddress: '',
                    preferredDate: '',
                    aaBoards: { left: 0, right: 0, straightOn: 0 },
                    fingerBoardCoverage: '',
                    maintenanceVisit: '',
                    specialRequirements: ''
                  });
                }}
                variant="outline"
                className="w-full"
              >
                Submit Another Request
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="bg-yellow-400 border-b-4 border-black">
        <div className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/aa-signs-logo.png" 
              alt="AA Signs Ltd Logo" 
              className="h-16 w-auto object-contain"
            />
            <div>
              <h1 className="text-2xl font-bold text-black">AA SIGNS LTD</h1>
              <p className="text-sm text-gray-800">Professional Road Signage</p>
            </div>
          </div>
          <Button 
            onClick={() => navigate('/')} 
            variant="outline"
            className="border-2 border-black"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-3">Request a Site Visit</h2>
          <p className="text-lg text-gray-600">Fill in the details below and we'll be in touch within 24 hours</p>
        </div>

        <Card className="border-2 border-gray-200 shadow-lg">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Information */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-yellow-400">
                  Contact Information
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="companyName">Company Name *</Label>
                    <Input
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      required
                      placeholder="Your Company Ltd"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contactPerson">Contact Person *</Label>
                    <Input
                      id="contactPerson"
                      name="contactPerson"
                      value={formData.contactPerson}
                      onChange={handleInputChange}
                      required
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="01234 567890"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="john@company.com"
                    />
                  </div>
                </div>
              </div>

              {/* Site Details */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-yellow-400">
                  Site Details
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="siteAddress">Site Address *</Label>
                    <Textarea
                      id="siteAddress"
                      name="siteAddress"
                      value={formData.siteAddress}
                      onChange={handleInputChange}
                      required
                      placeholder="Full site address including postcode"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label htmlFor="preferredDate">Preferred Installation Date</Label>
                    <Input
                      id="preferredDate"
                      name="preferredDate"
                      type="date"
                      value={formData.preferredDate}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              {/* AA Boards */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-yellow-400">
                  Black and Yellow Directional Signs Required
                </h3>
                <p className="text-sm text-gray-600 mb-4">Yellow directional signs with arrows and house logo</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="left">Left Signs</Label>
                    <Input
                      id="left"
                      type="number"
                      min="0"
                      value={formData.aaBoards.left}
                      onChange={(e) => handleAABoardChange('left', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="right">Right Signs</Label>
                    <Input
                      id="right"
                      type="number"
                      min="0"
                      value={formData.aaBoards.right}
                      onChange={(e) => handleAABoardChange('right', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="straightOn">Straight On Signs</Label>
                    <Input
                      id="straightOn"
                      type="number"
                      min="0"
                      value={formData.aaBoards.straightOn}
                      onChange={(e) => handleAABoardChange('straightOn', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              {/* Finger Boards */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-yellow-400">
                  Finger Board Coverage
                </h3>
                <p className="text-sm text-gray-600 mb-4">Placed on all major routes to site in all directions</p>
                <div className="grid md:grid-cols-3 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, fingerBoardCoverage: 'low' }))}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      formData.fingerBoardCoverage === 'low'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-300 hover:border-blue-400'
                    }`}
                  >
                    <div className="font-bold text-lg text-gray-900">Low Coverage</div>
                    <div className="text-3xl font-bold text-blue-600">25 Signs</div>
                    <div className="text-sm text-gray-600 mt-1">More spaced out</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, fingerBoardCoverage: 'medium' }))}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      formData.fingerBoardCoverage === 'medium'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-300 hover:border-blue-400'
                    }`}
                  >
                    <div className="font-bold text-lg text-gray-900">Medium Coverage</div>
                    <div className="text-3xl font-bold text-blue-600">50 Signs</div>
                    <div className="text-sm text-gray-600 mt-1">Balanced coverage</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, fingerBoardCoverage: 'heavy' }))}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      formData.fingerBoardCoverage === 'heavy'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-300 hover:border-blue-400'
                    }`}
                  >
                    <div className="font-bold text-lg text-gray-900">Heavy Coverage</div>
                    <div className="text-3xl font-bold text-blue-600">75 Signs</div>
                    <div className="text-sm text-gray-600 mt-1">Maximum visibility</div>
                  </button>
                </div>
              </div>

              {/* Maintenance Visits */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-yellow-400">
                  Site Maintenance Visits
                </h3>
                <p className="text-sm text-gray-600 mb-4">Regular visits to ensure signs remain visible and upright</p>
                <select
                  name="maintenanceVisit"
                  value={formData.maintenanceVisit}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Select maintenance option...</option>
                  <option value="one-off">One-off visit (higher price)</option>
                  <option value="weekly">Weekly maintenance visits</option>
                  <option value="bi-weekly">Bi-weekly maintenance visits</option>
                  <option value="monthly">Monthly maintenance visits</option>
                </select>
              </div>

              {/* Special Requirements */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-yellow-400">
                  Additional Information
                </h3>
                <div>
                  <Label htmlFor="specialRequirements">Special Requirements or Notes</Label>
                  <Textarea
                    id="specialRequirements"
                    name="specialRequirements"
                    value={formData.specialRequirements}
                    onChange={handleInputChange}
                    placeholder="Any specific requirements, access information, or questions..."
                    rows={4}
                  />
                </div>
              </div>

              {/* Payment Terms Notice */}
              <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
                <p className="text-sm text-gray-800">
                  <strong>Payment Terms:</strong> 30-day payment terms on all accounts
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-semibold"
                >
                  {loading ? 'Submitting...' : 'Submit Site Visit Request'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default SiteVisitRequest;
